import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ArticlesProvider } from './hooks/useArticles';
import { ToastProvider } from './hooks/useToast';
import { ConfirmProvider } from './hooks/useConfirm';
import { ThemeProvider } from './hooks/useTheme';
import { setTitle, getEnvironmentClasses } from './utils/environment';
import { resetUnderLock, triggerDrain } from './services/sync';
import { attachSyncTriggers } from './services/syncTriggers';
import { getAccessTokenFromDb } from './services/tokenStore';
import Login from './pages/Login';
import Register from './pages/Register';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Dashboard from './pages/Dashboard';
import Stats from './pages/Stats';
import Topics from './pages/Topics';
import TopicDetail from './pages/TopicDetail';
import Briefing from './pages/Briefing';
import DailyBriefing from './pages/DailyBriefing';
import AllHighlights from './pages/AllHighlights';
import Feeds from './pages/Feeds';
import Sources from './pages/Sources';
import SourceArticles from './pages/SourceArticles';
import Settings from './pages/Settings';
import CostDashboard from './pages/CostDashboard';
import QueueDashboard from './pages/QueueDashboard';
import PricingManagement from './pages/PricingManagement';
import Newsletters from './pages/Newsletters';
import NewsletterDetail from './pages/NewsletterDetail';
import NewsletterCompose from './pages/NewsletterCompose';
import PendingIssues from './pages/PendingIssues';
import ArticleViewer from './components/ArticleViewer';
import OfflineBanner from './components/OfflineBanner';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page-bg">
        <div className="text-center">
          <div className="spinner-fun mx-auto mb-4"></div>
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? (
    <>
      <OfflineBanner />
      {children}
    </>
  ) : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page-bg">
        <div className="text-center">
          <div className="spinner-fun mx-auto mb-4"></div>
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/article/:id"
        element={
          <ProtectedRoute>
            <ArticleViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/topics"
        element={
          <ProtectedRoute>
            <Topics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/topics/:topicId"
        element={
          <ProtectedRoute>
            <TopicDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily"
        element={
          <ProtectedRoute>
            <DailyBriefing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily/:date"
        element={
          <ProtectedRoute>
            <DailyBriefing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/briefing"
        element={
          <ProtectedRoute>
            <Briefing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <Stats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/highlights"
        element={
          <ProtectedRoute>
            <AllHighlights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feeds"
        element={
          <ProtectedRoute>
            <Feeds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sources"
        element={
          <ProtectedRoute>
            <Sources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sources/:domain"
        element={
          <ProtectedRoute>
            <SourceArticles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsletters"
        element={
          <ProtectedRoute>
            <Newsletters />
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsletters/new"
        element={
          <ProtectedRoute>
            <NewsletterCompose />
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsletters/:id"
        element={
          <ProtectedRoute>
            <NewsletterDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-issues"
        element={
          <ProtectedRoute>
            <PendingIssues />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/queue"
        element={
          <ProtectedRoute>
            <QueueDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/costs"
        element={
          <ProtectedRoute>
            <CostDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <ProtectedRoute>
            <PricingManagement />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 fallback */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-page-bg">
            <div className="text-center">
              <div className="text-6xl font-bold text-tertiary-text mb-4">404</div>
              <h2 className="text-2xl font-semibold text-heading mb-2">Page Not Found</h2>
              <p className="text-secondary-text mb-6">
                The page you&apos;re looking for doesn&apos;t exist.
              </p>
              <Link
                to="/dashboard"
                className="btn-clay inline-flex items-center"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Set the title with environment prefix on app load
    setTitle();
  }, []);

  useEffect(() => {
    async function boot() {
      // Reason: sync IDB access token to LS so the foreground apiClient picks up
      // any token refreshed by the SW while the app was backgrounded/closed.
      try {
        const idbToken = await getAccessTokenFromDb();
        if (idbToken) localStorage.setItem('authToken', idbToken);
      } catch {
        // ignore — IDB unavailable or no token cached
      }
      resetUnderLock().then(() => triggerDrain()).catch(() => {});
    }
    boot();
    const detach = attachSyncTriggers();
    return detach;
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    function onSwMessage(event) {
      const { type, accessToken } = event.data || {};
      // Reason: SW refresh writes to IDB only; propagate to LS so the axios
      // client uses the fresh token on the next request.
      if (type === 'tokens-refreshed' && accessToken) {
        localStorage.setItem('authToken', accessToken);
      }
    }
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, []);

  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <ArticlesProvider>
            <ToastProvider>
              <ConfirmProvider>
                <div className={`App ${getEnvironmentClasses()}`}>
                  <AppRoutes />
                </div>
              </ConfirmProvider>
            </ToastProvider>
          </ArticlesProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;